import { useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, ListItemText, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Check, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppLanguage } from '../../i18n/useAppLanguage';

interface LanguageSwitcherProps {
  /**
   * `menu`   — icon button + dropdown. For the app top bar, where space is tight.
   * `inline` — the languages laid out as visible buttons. For sign-in, registration and the
   *            check-in kiosk: someone who reads only Telugu cannot be asked to find a
   *            dropdown behind an English tooltip, so pre-auth screens show the choice outright.
   */
  variant?: 'menu' | 'inline';
  size?: 'small' | 'medium';
  /** Overrides the icon button colour so the switcher can sit on a dark app bar. */
  color?: string;
  /** Styling for the trigger button, so the switcher can match the bar it is placed in. */
  buttonSx?: SxProps<Theme>;
}

export function LanguageSwitcher({ variant = 'menu', size = 'small', color, buttonSx }: LanguageSwitcherProps) {
  const { t } = useTranslation('common');
  const { code, languages, setLanguage } = useAppLanguage();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (variant === 'inline') {
    return (
      <Box
        role="group"
        aria-label={t('language.changeLanguage')}
        sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}
      >
        {languages.map((lang) => {
          const selected = lang.code === code;
          return (
            <Button
              key={lang.code}
              size={size}
              onClick={() => setLanguage(lang.code)}
              aria-pressed={selected}
              // The endonym is the label a speaker of that language actually recognises,
              // so it is never translated and never replaced by the English name.
              lang={lang.code}
              sx={{
                minWidth: 0,
                px: 1.5,
                textTransform: 'none',
                fontWeight: selected ? 700 : 500,
                color: selected ? 'primary.main' : 'text.secondary',
                bgcolor: selected ? 'action.selected' : 'transparent',
                borderRadius: 1.5,
              }}
            >
              {lang.label}
            </Button>
          );
        })}
      </Box>
    );
  }

  return (
    <>
      <Tooltip title={t('language.changeLanguage')}>
        <IconButton
          size={size}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={t('language.changeLanguage')}
          aria-haspopup="menu"
          sx={{ color: color ?? 'inherit', ...buttonSx }}
        >
          <Languages size={size === 'small' ? 16 : 20} />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === code}
            onClick={() => {
              setLanguage(lang.code);
              setAnchorEl(null);
            }}
            sx={{ gap: 1.5, minWidth: 200 }}
          >
            <ListItemText
              primary={
                <Typography lang={lang.code} sx={{ fontWeight: lang.code === code ? 700 : 500 }}>
                  {lang.label}
                </Typography>
              }
              secondary={lang.englishLabel === lang.label ? undefined : lang.englishLabel}
            />
            {lang.code === code && <Check size={16} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
